import { useEffect, useState } from 'react'
import { getClients, createClient } from '../../services/api'
import { Client } from '../../types'
import { Plus, Users, HardDrive, Mail } from 'lucide-react'

export default function ClientManager() {
  const [clients, setClients] = useState<Client[]>([])
  const [showNew, setShowNew] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getClients()
      .then(res => setClients(res.data.clients))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    try {
      const res = await createClient({
        name: data.get('name') as string,
        slug: data.get('slug') as string,
        contact_email: data.get('contact_email') as string,
        storage_quota_gb: parseInt(data.get('storage_quota_gb') as string) || 100,
      })
      setClients([...clients, { ...res.data, used_storage_gb: 0, is_active: true }])
      setShowNew(false)
      form.reset()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create client')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-800">Clients</h2>
        <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          New Client
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map(client => (
          <div key={client.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-lg bg-navy-100 text-navy-700">
                <Users size={20} />
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                client.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'
              }`}>
                {client.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <h3 className="font-semibold text-navy-800">{client.name}</h3>
            <p className="text-sm text-gray-500 mt-1">{client.slug}</p>

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <Mail size={14} className="mr-2" />
                {client.contact_email || 'No email'}
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <HardDrive size={14} className="mr-2" />
                {client.used_storage_gb.toFixed(1)} / {client.storage_quota_gb} GB
              </div>
            </div>

            <div className="mt-4 w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min((client.used_storage_gb / client.storage_quota_gb) * 100, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* New Client Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-navy-800 mb-4">Create New Client</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="label">Client Name</label>
                <input name="name" type="text" className="input" required />
              </div>
              <div>
                <label className="label">Slug (URL-friendly)</label>
                <input name="slug" type="text" className="input" placeholder="e.g., golf-estate-1" required />
                <p className="text-xs text-gray-400 mt-1">Lowercase, numbers, hyphens only</p>
              </div>
              <div>
                <label className="label">Contact Email</label>
                <input name="contact_email" type="email" className="input" />
              </div>
              <div>
                <label className="label">Storage Quota (GB)</label>
                <input name="storage_quota_gb" type="number" className="input" defaultValue={100} min={1} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="btn-outline flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
